import { useMemo } from 'react';
import type { CommissionType } from '../types/multiInfluencer';
import {
  calculateInfluencerCommission,
  calculateInfluencerNetPay,
} from '../utils/commissionCalculations';

export { calculateInfluencerCommission, calculateInfluencerNetPay };

export interface InfluencerCostData {
  agreed_fee: number;
  commission_pct: number;
  commission_flat_fee: number;
  commission_type: CommissionType;
}

export interface ServiceCostData {
  agreed_fee: number;
}

export interface CampaignCalculationResult {
  totalInfluencerCosts: number;
  totalServiceCosts: number;
  totalCosts: number;
  totalInfluencerCommissions: number;
  totalNetToInfluencers: number;
  operationalMargin: number;
  isCommissionOnlyMode: boolean;
  marginIsPositive: boolean;
}

export function useCampaignCalculations(
  clientBudget: number,
  influencers: InfluencerCostData[],
  services: ServiceCostData[]
): CampaignCalculationResult {
  return useMemo(() => {
    const totalInfluencerCosts = influencers.reduce(
      (total, inf) => total + (inf.agreed_fee || 0),
      0
    );

    const totalServiceCosts = services.reduce(
      (total, svc) => total + (svc.agreed_fee || 0),
      0
    );

    const totalCosts = totalInfluencerCosts + totalServiceCosts;

    const totalInfluencerCommissions = influencers.reduce((total, inf) => {
      const commission = calculateInfluencerCommission(
        inf.agreed_fee || 0,
        inf.commission_pct ?? 0,
        inf.commission_flat_fee ?? 0,
        inf.commission_type ?? 'percentage'
      );
      return total + commission;
    }, 0);

    const totalNetToInfluencers = influencers.reduce((total, inf) => {
      const netPay = calculateInfluencerNetPay(
        inf.agreed_fee || 0,
        inf.commission_pct ?? 0,
        inf.commission_flat_fee ?? 0,
        inf.commission_type ?? 'percentage'
      );
      return total + netPay;
    }, 0);

    const isCommissionOnlyMode = clientBudget === 0 && influencers.length > 0;

    let operationalMargin: number;
    if (isCommissionOnlyMode) {
      operationalMargin = totalInfluencerCommissions;
    } else {
      operationalMargin = (clientBudget - totalCosts) + totalInfluencerCommissions;
    }

    const marginIsPositive = isCommissionOnlyMode ? true : operationalMargin >= 0;

    return {
      totalInfluencerCosts,
      totalServiceCosts,
      totalCosts,
      totalInfluencerCommissions,
      totalNetToInfluencers,
      operationalMargin,
      isCommissionOnlyMode,
      marginIsPositive,
    };
  }, [clientBudget, influencers, services]);
}
